// WinnerSelect.js
import React, { useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import { Link } from "react-router-dom";
import { saveCountdownPayloadToStore } from "../utils/countdownPayloadStore";
import {
  buildCompatibilityMessage,
  CSV_MISSING_COLUMNS_MESSAGE,
  detectEmailKey,
  detectEntriesKey,
  detectNameKey,
  extractSweepstakesMetadataFromRow,
  inspectCsvCompatibility,
  mergeSweepstakesMetadata,
  prepareCsvSourceForParsing,
} from "../utils/winnerSelectCsvValidation";
import "./WinnerSelect.css";

const COUNTDOWN_PAYLOAD_KEY = "cs_countdown_payload_v1";
const BRANDING_KEY = "cs_countdown_branding_v1";
const ANALYTICS_TARGET_EMAIL =
  "C8Cb2EDB-6BA7-413D-8444-5F8D0AA9CD7B/85962023";
const SHOW_TARGET_ANALYTICS = false;
const PERCENT_DISPLAY_DECIMALS = 4;

export default function WinnerSelect() {
  const [fileName, setFileName] = useState("");
  const [aggregated, setAggregated] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [error, setError] = useState("");
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const hasCrypto =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.getRandomValues === "function";
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  const [fileUploadInfo, setFileUploadInfo] = useState({
    name: "",
    uploadedAt: 0,
  });
  const [exportMetadata, setExportMetadata] = useState(createEmptySweepstakesMetadata());

  // Branding/logo (saved to localStorage)
  const [brandingName, setBrandingName] = useState("");
  const [brandingDataUrl, setBrandingDataUrl] = useState("");

  const fileInputRef = useRef(null);
  const brandingInputRef = useRef(null);
  const activeParseRef = useRef(null);
  const parseRunRef = useRef(0);

  // Load branding from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(BRANDING_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.dataUrl) setBrandingDataUrl(String(parsed.dataUrl));
      if (parsed?.name) setBrandingName(String(parsed.name));
    } catch {
      // ignore
    }
  }, []);

  const [detected, setDetected] = useState({
    emailKey: "",
    entriesKey: "",
    nameKey: "",
  });
  const [targetAnalytics, setTargetAnalytics] = useState(
    createEmptyTargetAnalytics()
  );
  const [reportAnalytics, setReportAnalytics] = useState(
    createEmptyReportAnalytics()
  );

  async function onFileSelected(file) {
    if (!file) return;

    parseRunRef.current += 1;
    const runId = parseRunRef.current;
    const uploadedAt = Date.now();

    if (activeParseRef.current?.abort) activeParseRef.current.abort();

    setError("");
    setSelectedFile(file);
    setFileUploadInfo({
      name: file.name,
      uploadedAt,
    });
    setFileName(file.name);
    setAggregated([]);
    setTotalEntries(0);
    setDetected({ emailKey: "", entriesKey: "", nameKey: "" });
    setTargetAnalytics(createEmptyTargetAnalytics());
    setReportAnalytics(createEmptyReportAnalytics());
    setCompatibilityResult(null);
    setExportMetadata(createEmptySweepstakesMetadata());
    setIsProcessingCsv(true);

    try {
      const parsed = await parseAndAggregateCsvFile(file, (parser) => {
        if (parseRunRef.current !== runId) {
          parser.abort();
          return;
        }
        activeParseRef.current = parser;
      });

      if (parseRunRef.current !== runId) return;

      setError("");
      setAggregated(parsed.aggregated);
      setTotalEntries(parsed.totalEntries);
      setDetected({
        emailKey: parsed.emailKey,
        entriesKey: parsed.entriesKey,
        nameKey: parsed.nameKey,
      });
      setTargetAnalytics(parsed.targetAnalytics);
      setReportAnalytics(parsed.reportAnalytics);
      setExportMetadata(parsed.exportMetadata);
    } catch (e) {
      if (parseRunRef.current !== runId) return;
      try {
        localStorage.removeItem(COUNTDOWN_PAYLOAD_KEY);
      } catch {}
      const message = String(e?.message || e || "Failed to parse CSV.");
      setError(message === CSV_MISSING_COLUMNS_MESSAGE ? "" : message);
      setAggregated([]);
      setTotalEntries(0);
      setDetected({ emailKey: "", entriesKey: "", nameKey: "" });
      setTargetAnalytics(createEmptyTargetAnalytics());
      setReportAnalytics(createEmptyReportAnalytics());
      setExportMetadata(createEmptySweepstakesMetadata());
    } finally {
      if (parseRunRef.current === runId) {
        activeParseRef.current = null;
        setIsProcessingCsv(false);
      }
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) onFileSelected(f);
  }

  function onBrandingSelected(file) {
    if (!file) return;

    // quick sanity check: avoid huge images (localStorage limits are small)
    if (file.size > 1750 * 1024) {
      setError("Branding image is too large. Please use an image under 750KB.");
      return;
    }

    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = String(e.target?.result || "");
      setBrandingName(file.name);
      setBrandingDataUrl(dataUrl);

      try {
        localStorage.setItem(
          BRANDING_KEY,
          JSON.stringify({
            name: file.name,
            dataUrl,
            updatedAt: Date.now(),
          })
        );
      } catch {
        setError(
          "Could not save branding to local storage (image may be too large). Try a smaller image."
        );
      }
    };
    reader.onerror = () => setError("Failed to read branding image.");
    reader.readAsDataURL(file);
  }

  function clearBranding() {
    setBrandingName("");
    setBrandingDataUrl("");
    try {
      localStorage.removeItem(BRANDING_KEY);
    } catch {}
    if (brandingInputRef.current) brandingInputRef.current.value = "";
  }

  async function startCountdownScreenInNewTab() {
    if (!aggregated.length) {
      setError("Please upload a valid CSV first.");
      return;
    }
    if (totalEntries <= 0) {
      setError("All rows have zero entries.");
      return;
    }

    const payload = {
      aggregated,
      emailKey: detected.emailKey,
      entriesKey: detected.entriesKey,
      nameKey: detected.nameKey,
      fileName: fileUploadInfo.name || fileName,
      fileUploadedAt: fileUploadInfo.uploadedAt,
      reportAnalytics,
      exportMetadata,
      createdAt: Date.now(),
    };

    try {
      const pointer = await saveCountdownPayloadToStore(payload);
      localStorage.setItem(COUNTDOWN_PAYLOAD_KEY, JSON.stringify(pointer));
    } catch {
      try {
        // Backward-compatible fallback for environments where IndexedDB is unavailable.
        localStorage.setItem(COUNTDOWN_PAYLOAD_KEY, JSON.stringify(payload));
      } catch {
        setError(
          "Could not save countdown payload to local storage (CSV might be too large)."
        );
        return;
      }
    }

    // Open countdown in a separate tab
    const url = `${window.location.origin}/winner-select/countdown`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function checkSelectedCsvCompatibility() {
    if (!selectedFile) {
      setCompatibilityResult({
        ok: false,
        message: "Choose a CSV first.",
      });
      return;
    }

    setCompatibilityResult(null);
    setIsCheckingCompatibility(true);

    try {
      const structure = await inspectCsvCompatibility(selectedFile, {
        requireDataUnderHeader: true,
      });

      setCompatibilityResult({
        ok: true,
        message: buildCompatibilityMessage(structure),
      });
    } catch (e) {
      setCompatibilityResult({
        ok: false,
        message: String(e?.message || e || "Failed to inspect CSV."),
      });
    } finally {
      setIsCheckingCompatibility(false);
    }
  }

  function onReset() {
    parseRunRef.current += 1;
    if (activeParseRef.current?.abort) activeParseRef.current.abort();
    activeParseRef.current = null;
    try {
      localStorage.removeItem(COUNTDOWN_PAYLOAD_KEY);
    } catch {}

    setSelectedFile(null);
    setFileName("");
    setAggregated([]);
    setTotalEntries(0);
    setError("");
    setDetected({ emailKey: "", entriesKey: "", nameKey: "" });
    setTargetAnalytics(createEmptyTargetAnalytics());
    setReportAnalytics(createEmptyReportAnalytics());
    setExportMetadata(createEmptySweepstakesMetadata());
    setCompatibilityResult(null);
    setIsProcessingCsv(false);
    setFileUploadInfo({
      name: "",
      uploadedAt: 0,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";

    // (intentionally NOT clearing branding; you asked to save it to local storage)
  }

  useEffect(() => {
    return () => {
      parseRunRef.current += 1;
      if (activeParseRef.current?.abort) activeParseRef.current.abort();
      activeParseRef.current = null;
    };
  }, []);

  return (
    <main className="ws-root">
      <header className="ws-header">
        <h1>Winner Select</h1>
        <p className="ws-sub">
          Pick a weighted winner from your entrants CSV — everything runs in your
          browser.
        </p>
      </header>

      <section className="ws-accordions">
        <details className="ws-card" open>
          <summary>How this tool works</summary>
          <div className="ws-card-body">
            <ol className="ws-list">
              <li>
                Download CSV from the <strong>"Select Winner"</strong> tab in
                the Clean Sweeps Shopify admin app.
              </li>
              <li>
              <strong>(Optional)</strong> ) Convert it with our tool to combine entry rows (
                <Link to="/convert">Convert tool</Link>).{" "}
                <em>Note: This step is not necessary. Our winner select tool accounts for weighted multiple rows.</em>
              </li>
              <li><strong>(Optional)</strong> Upload your company logo or branding to show on the winner select page</li>
              <li>
                Upload your finalized CSV file in the area below.{" "}
                <strong>Note: The columns/column headers must NOT be altered</strong>
              </li>
              <li>
                Press <strong>Select Winner</strong>.
              </li>
              <li>In the new tab, press <strong>Start Countdown</strong>.</li>
              <li>Winner is selected (private data is blurred).</li>
            </ol>
          </div>
        </details>

        <details className="ws-card">
          <summary>Disclaimers & Recommendations</summary>
          <div className="ws-card-body">
            <ul className="ws-list">
              <li>
                <strong>Test before live:</strong> We recommend testing this tool
                before any live stream.
              </li>
              <li>
                <strong>Excluded states:</strong> If your sweepstakes excludes
                certain states, we are not responsible if an ineligible winner
                is selected.
              </li>
              <li>
                <strong>Eligibility review:</strong> Verify winner eligibility
                against your Official Rules before announcing.
              </li>
              <li>
                <strong>Recordkeeping:</strong> Keep your original CSV and a copy
                of the final draw results for audit/compliance.
              </li>
            </ul>
          </div>
        </details>

        {/* NOTE: fixed class -> className to avoid JSX issues */}
        <details className="ws-card">
          <summary>Privacy &amp; Terms</summary>
          <div className="ws-card-body">
            <p>
              <strong>Privacy.</strong> All processing happens in your browser;
              files are not uploaded to our servers.
              <strong>No warranties; use at your own risk.</strong> This tool is
              provided “AS IS” and “AS AVAILABLE,” without any warranties.
              <strong>Administration</strong> Clean Sweeps provides software tools
              and entry-record services only. We do not sponsor, or operate any
              sweepstakes or promotions.
              <strong>
                By using this tool you agree to our{" "}
                <Link to="/tos">Terms of Service</Link>.
              </strong>
            </p>
          </div>
        </details>
      </section>

      <div className="ws-accept" style={{ marginTop: 12 }}>
        <input
          id="ws-accept"
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
        />
        <label htmlFor="ws-accept">
          I have read and <strong>agree</strong> to the Privacy &amp; Terms above
          (required)
        </label>
      </div>

      {/* NEW: Branding upload */}
      <p className="ws-branding-label">
        Add a branding/logo image to the winner selection screen (optional)
      </p>
      <section className="ws-uploader ws-branding">
        <div className="ws-drop">
          <input
            ref={brandingInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => onBrandingSelected(e.target.files?.[0])}
          />

          <div className="ws-drop-hint">
            <div className="ws-file-name">
              {brandingName || "No branding/logo selected"}
            </div>

            {brandingDataUrl && (
              <div className="ws-branding-preview-wrap">
                <img
                  className="ws-branding-preview"
                  src={brandingDataUrl}
                  alt="Branding / logo preview"
                />
              </div>
            )}

            <div className="ws-actions">
              <button
                className="ws-btn"
                onClick={() => brandingInputRef.current?.click()}
                type="button"
              >
                Attach branding/logo
              </button>

              {brandingDataUrl && (
                <button
                  className="ws-btn ghost"
                  onClick={clearBranding}
                  type="button"
                  title="Remove branding"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CSV Upload */}
      <section
        className="ws-uploader"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <div className="ws-drop">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => onFileSelected(e.target.files?.[0])}
          />
          <div className="ws-drop-hint">
            <div className="ws-file-name">{fileName || "No file selected"}</div>
            <div className="ws-actions">
              <button
                className="ws-btn"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                Choose CSV
              </button>
              <span className="ws-or">or</span>
              <span className="ws-drag-label">Drag & Drop here</span>
            </div>
          </div>
        </div>
      </section>

      {isProcessingCsv && (
        <p className="ws-hint">Please wait, processing CSV...</p>
      )}

      {!isProcessingCsv && (!aggregated.length || totalEntries <= 0) && (
        <p className="ws-hint">Upload a valid CSV to enable</p>
      )}

      {SHOW_TARGET_ANALYTICS &&
        !isProcessingCsv &&
        aggregated.length > 0 &&
        totalEntries > 0 && (
        <section className="ws-card" style={{ marginTop: 12 }}>
          <div className="ws-card-body">
            <h3 style={{ marginTop: 0 }}>Target Analytics</h3>
            <p style={{ marginTop: 0, overflowWrap: "anywhere" }}>
              Tracking entrant key: <strong>{ANALYTICS_TARGET_EMAIL}</strong>
            </p>
            <div
              style={{
                display: "grid",
                gap: 6,
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              }}
            >
              <div>
                Total pool entries:{" "}
                <strong>{formatInteger(totalEntries)}</strong>
              </div>
              <div>
                Unique entrants:{" "}
                <strong>{formatInteger(aggregated.length)}</strong>
              </div>
              <div>
                Raw CSV rows seen:{" "}
                <strong>{formatInteger(targetAnalytics.rawRowsSeen)}</strong>
              </div>
              <div>
                Rows with usable data:{" "}
                <strong>{formatInteger(targetAnalytics.dataRows)}</strong>
              </div>
              <div>
                Rows counted in pool:{" "}
                <strong>{formatInteger(targetAnalytics.poolRows)}</strong>
              </div>
              <div>
                Rows ignored:{" "}
                <strong>{formatInteger(targetAnalytics.ignoredRows)}</strong>
              </div>
              <div>
                Ignored empty rows:{" "}
                <strong>{formatInteger(targetAnalytics.emptyRowsIgnored)}</strong>
              </div>
              <div>
                Ignored blank-email rows:{" "}
                <strong>{formatInteger(targetAnalytics.blankEmailRowsIgnored)}</strong>
              </div>
              <div>
                Zero-entry rows:{" "}
                <strong>{formatInteger(targetAnalytics.zeroEntryRows)}</strong>
              </div>
              <div>
                Exact key entries:{" "}
                <strong>{formatInteger(targetAnalytics.exactEntries)}</strong>
              </div>
              <div>
                Exact key chance:{" "}
                <strong>
                  {formatSharePercent(targetAnalytics.exactEntries, totalEntries)}
                </strong>
              </div>
              <div>
                Matching CSV rows:{" "}
                <strong>{formatInteger(targetAnalytics.exactRows)}</strong>
              </div>
              <div>
                Matching pooled rows:{" "}
                <strong>{formatInteger(targetAnalytics.exactPoolRows)}</strong>
              </div>
            </div>

            <p style={{ marginBottom: 8 }}>
              Exact key contributes{" "}
              <strong>{formatInteger(targetAnalytics.exactEntries)}</strong>{" "}
              pooled entries ({formatSharePercent(targetAnalytics.exactEntries, totalEntries)}).
            </p>

            <p style={{ marginBottom: 8 }}>
              After trimming leading/trailing commas and semicolons, the same
              entrant family totals{" "}
              <strong>{formatInteger(targetAnalytics.familyEntries)}</strong>{" "}
              entries across{" "}
              <strong>{formatInteger(targetAnalytics.familyRows)}</strong> raw rows (
              {formatSharePercent(targetAnalytics.familyEntries, totalEntries)}).
            </p>

            <p style={{ marginBottom: 8 }}>
              The current parser ignores fully empty rows and rows with a blank
              entrant key. Zero-entry rows are still kept as entrants if the key
              exists, but they contribute 0 to the pool.
            </p>

            {targetAnalytics.variants.length > 0 ? (
              <>
                <p style={{ margin: "8px 0" }}>
                  Related variants found that would be treated as separate
                  entrants today:
                </p>
                <ul className="ws-list" style={{ marginTop: 0 }}>
                  {targetAnalytics.variants.map((variant) => (
                    <li key={variant.key} style={{ overflowWrap: "anywhere" }}>
                      <strong>{variant.key}</strong>:{" "}
                      {formatInteger(variant.entries)} entries across{" "}
                      {formatInteger(variant.rows)} rows (
                      {formatSharePercent(variant.entries, totalEntries)})
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p style={{ marginBottom: 0 }}>
                No comma/semicolon variants of this entrant key were found.
              </p>
            )}
          </div>
        </section>
      )}

      <section className="ws-actions-bar">
        <button
          className="ws-btn"
          onClick={checkSelectedCsvCompatibility}
          disabled={isProcessingCsv || isCheckingCompatibility || !selectedFile}
          type="button"
        >
          {isCheckingCompatibility ? "Checking CSV..." : "Check CSV Compatibility"}
        </button>

        <button
          className="ws-btn primary"
          onClick={startCountdownScreenInNewTab}
          disabled={
            isProcessingCsv ||
            !aggregated.length ||
            totalEntries <= 0 ||
            !hasCrypto ||
            !acceptedTerms
          }
          title={!acceptedTerms ? "Please agree to the Terms & Conditions" : undefined}
          type="button"
        >
          Select Winner (opens new tab)
        </button>

        <button className="ws-btn ghost" onClick={onReset} type="button">
          Reset
        </button>
      </section>

      {compatibilityResult &&
        (compatibilityResult.ok ? (
          <p className="ws-hint" style={{ marginTop: 12 }}>
            {compatibilityResult.message}
          </p>
        ) : (
          <div className="ws-error" style={{ marginTop: 12 }}>
            {compatibilityResult.message}
          </div>
        ))}

      {error && <div className="ws-error">{error}</div>}
    </main>
  );
}

/* -------- parsing + helpers -------- */

function parseAndAggregateCsvFile(file, onParser) {
  return prepareCsvSourceForParsing(file).then(({ source, useWorker, exportMetadata }) => {
    return new Promise((resolve, reject) => {
      const debugAnalyticsEnabled = SHOW_TARGET_ANALYTICS;
      let headers = [];
      let emailKey = "";
      let entriesKey = "";
      let nameKey = "";
      let missingRequired = false;
      const aggregatedMap = new Map();
      let totalEntries = 0;
      let rawRowsSeen = 0;
      let dataRows = 0;
      let poolRows = 0;
      let emptyRowsIgnored = 0;
      let blankEmailRowsIgnored = 0;
      const blankEmailRows = [];
      let zeroEntryRows = 0;
      const targetKey = debugAnalyticsEnabled
        ? normalizeEntrantKey(ANALYTICS_TARGET_EMAIL)
        : "";
      const targetFamilyKey = debugAnalyticsEnabled
        ? normalizeAnalyticsFamilyKey(ANALYTICS_TARGET_EMAIL)
        : "";
      let exactEntries = 0;
      let exactRows = 0;
      let exactPoolRows = 0;
      let familyEntries = 0;
      let familyRows = 0;
      const familyVariantMap = debugAnalyticsEnabled ? new Map() : null;
      const rowStatsByEmail = new Map();

      Papa.parse(source, {
        header: true,
        skipEmptyLines: "greedy",
        worker: useWorker,
        dynamicTyping: false,
        step: (result, parser) => {
          onParser?.(parser);
          rawRowsSeen += 1;

          if (!headers.length) {
            headers = (result?.meta?.fields || Object.keys(result?.data || {})).map((h) =>
              String(h ?? "").trim()
            );

            emailKey = detectEmailKey(headers);
            entriesKey = detectEntriesKey(headers);
            nameKey = detectNameKey(headers);

            if (!emailKey || !entriesKey) {
              missingRequired = true;
              parser.abort();
              return;
            }
          }

          if (!result?.data || typeof result.data !== "object") return;
          const row = sanitizeParsedRow(result.data);
          if (!row) {
            emptyRowsIgnored += 1;
            return;
          }
          dataRows += 1;

          const normalizedEmail = normalizeEntrantKey(row[emailKey]);
          const rowEntries = Math.max(0, toNumber(row[entriesKey]));
          const hasEmail = normalizedEmail !== "";
          const contributesToPool = hasEmail;

          if (!hasEmail) {
            blankEmailRowsIgnored += 1;
            blankEmailRows.push(
              createBlankEmailRowSummary({
                row,
                nameKey,
                entries: rowEntries,
                rowNumber: rawRowsSeen,
              })
            );
          } else {
            poolRows += 1;
            const existingRowStats = rowStatsByEmail.get(normalizedEmail) || {
              entries: 0,
              rows: 0,
            };
            existingRowStats.entries += rowEntries;
            existingRowStats.rows += 1;
            rowStatsByEmail.set(normalizedEmail, existingRowStats);
          }

          if (hasEmail && rowEntries <= 0) {
            zeroEntryRows += 1;
          }

          if (debugAnalyticsEnabled) {
            if (normalizedEmail === targetKey) {
              exactEntries += rowEntries;
              exactRows += 1;
              if (contributesToPool) exactPoolRows += 1;
            }

            const familyKey = normalizeAnalyticsFamilyKey(normalizedEmail);
            if (familyKey && familyKey === targetFamilyKey) {
              familyEntries += rowEntries;
              familyRows += 1;

              const existing = familyVariantMap.get(normalizedEmail) || {
                key: normalizedEmail,
                entries: 0,
                rows: 0,
              };
              existing.entries += rowEntries;
              existing.rows += 1;
              familyVariantMap.set(normalizedEmail, existing);
            }
          }

          totalEntries += aggregateRowByEmail(aggregatedMap, row, emailKey, entriesKey);
        },
        complete: (results) => {
          if (!headers.length) {
            headers = (results?.meta?.fields || []).map((h) => String(h ?? "").trim());
            emailKey = detectEmailKey(headers);
            entriesKey = detectEntriesKey(headers);
            nameKey = detectNameKey(headers);
          }

          if (missingRequired || !emailKey || !entriesKey) {
            reject(new Error(CSV_MISSING_COLUMNS_MESSAGE));
            return;
          }

          const aggregatedRows = Array.from(aggregatedMap.values());
          const rowMetadata = extractSweepstakesMetadataFromRow(aggregatedRows[0] || null);

          resolve({
            headers,
            emailKey,
            entriesKey,
            nameKey,
            totalEntries,
            aggregated: aggregatedRows,
            exportMetadata: mergeSweepstakesMetadata(exportMetadata, rowMetadata),
            reportAnalytics: {
              rawRowsSeen,
              dataRows,
              poolRows,
              ignoredRows: emptyRowsIgnored + blankEmailRowsIgnored,
              emptyRowsIgnored,
              blankEmailRowsIgnored,
              blankEmailRows,
              zeroEntryRows,
              rowStatsByEmail: Object.fromEntries(rowStatsByEmail.entries()),
            },
            targetAnalytics: debugAnalyticsEnabled
              ? {
                  targetKey,
                  rawRowsSeen,
                  dataRows,
                  poolRows,
                  ignoredRows: emptyRowsIgnored + blankEmailRowsIgnored,
                  emptyRowsIgnored,
                  blankEmailRowsIgnored,
                  zeroEntryRows,
                  exactEntries,
                  exactRows,
                  exactPoolRows,
                  familyEntries,
                  familyRows,
                  variants: Array.from(familyVariantMap.values())
                    .filter((variant) => variant.key !== targetKey)
                    .sort((a, b) => b.entries - a.entries),
                }
              : createEmptyTargetAnalytics(),
          });
        },
        error: (err) => reject(err || new Error("Failed to parse CSV.")),
      });
    });
  });
}

function sanitizeParsedRow(data) {
  const out = {};
  let hasValue = false;

  Object.keys(data || {}).forEach((rawKey) => {
    if (rawKey === "__parsed_extra__") return;
    const key = String(rawKey ?? "").trim();
    const value = String(data[rawKey] ?? "").trim();
    if (value) hasValue = true;
    out[key] = value;
  });

  return hasValue ? out : null;
}

function aggregateRowByEmail(map, row, emailKey, entriesKey) {
  const email = normalizeEntrantKey(row[emailKey]);
  if (!email) return 0;

  const add = Math.max(0, toNumber(row[entriesKey]));
  if (!map.has(email)) {
    const base = { ...row };
    base[entriesKey] = add;
    map.set(email, base);
    return add;
  }

  const prev = map.get(email);
  prev[entriesKey] = Math.max(0, toNumber(prev[entriesKey]) + add);
  mergeMissingIdentityFields(prev, row);
  return add;
}

function mergeMissingIdentityFields(target, source) {
  fillMissingField(target, source, ["first name", "first_name", "firstname", "first"]);
  fillMissingField(target, source, ["last name", "last_name", "lastname", "last"]);
  fillMissingField(target, source, [
    "name",
    "full_name",
    "fullname",
    "customer_name",
    "customername",
  ]);
}

function fillMissingField(target, source, candidates) {
  const sourceKey = findKeyByCandidates(source, candidates);
  if (!sourceKey || !hasValue(source[sourceKey])) return;

  const targetKey = findKeyByCandidates(target, candidates) || sourceKey;
  if (!hasValue(target[targetKey])) {
    target[targetKey] = source[sourceKey];
  }
}

function findKeyByCandidates(obj, candidates) {
  const map = new Map(Object.keys(obj || {}).map((k) => [normalizeKey(k), k]));
  for (const c of candidates) {
    const found = map.get(normalizeKey(c));
    if (found) return found;
  }
  return null;
}

function normalizeKey(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function hasValue(v) {
  return String(v || "").trim() !== "";
}

function toNumber(v) {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return 0;
  const n = Number(v.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function normalizeEntrantKey(v) {
  return String(v || "").trim().toLowerCase();
}

function normalizeAnalyticsFamilyKey(v) {
  return normalizeEntrantKey(v).replace(/^[,;]+|[,;]+$/g, "");
}

function createBlankEmailRowSummary({ row, nameKey, entries, rowNumber }) {
  const firstKey = findKeyByCandidates(row, ["first name", "first_name", "firstname", "first"]);
  const lastKey = findKeyByCandidates(row, ["last name", "last_name", "lastname", "last"]);
  const first = firstKey ? String(row?.[firstKey] || "").trim() : "";
  const last = lastKey ? String(row?.[lastKey] || "").trim() : "";
  const fullName = [first, last].filter(Boolean).join(" ");
  const fallbackName =
    nameKey && nameKey !== "__first_last__" ? String(row?.[nameKey] || "").trim() : "";

  return {
    rowNumber: Math.max(0, Number(rowNumber) || 0),
    name: fullName || fallbackName,
    entries: Math.max(0, Number(entries) || 0),
  };
}

function createEmptyTargetAnalytics() {
  return {
    targetKey: normalizeEntrantKey(ANALYTICS_TARGET_EMAIL),
    rawRowsSeen: 0,
    dataRows: 0,
    poolRows: 0,
    ignoredRows: 0,
    emptyRowsIgnored: 0,
    blankEmailRowsIgnored: 0,
    zeroEntryRows: 0,
    exactEntries: 0,
    exactRows: 0,
    exactPoolRows: 0,
    familyEntries: 0,
    familyRows: 0,
    variants: [],
  };
}

function createEmptyReportAnalytics() {
  return {
    rawRowsSeen: 0,
    dataRows: 0,
    poolRows: 0,
    ignoredRows: 0,
    emptyRowsIgnored: 0,
    blankEmailRowsIgnored: 0,
    blankEmailRows: [],
    zeroEntryRows: 0,
    rowStatsByEmail: {},
  };
}

function createEmptySweepstakesMetadata() {
  return {
    sweepstakesId: "",
    sweepstakesName: "",
    sweepstakesStartDate: "",
    sweepstakesEndDate: "",
  };
}

function formatInteger(v) {
  return new Intl.NumberFormat().format(Math.max(0, Number(v) || 0));
}

function formatSharePercent(entries, total) {
  if (!total) return "0.000000%";
  return `${((Math.max(0, Number(entries) || 0) / total) * 100).toFixed(
    PERCENT_DISPLAY_DECIMALS
  )}%`;
}
